import DonutProgressCards from "../../../SSIUpline/DonutProgressCard/DonutProgressCards";
import { IconSquareCheck } from "../../../../common/icons/IconSquareCheck/IconSquareCheck";
import { IconError } from "../../../../common/icons/IconError/IconError";
import { IconWarningTriangle } from "../../../../common/icons/IconWarningTriangle/IconWarningTriangle";
import { AboKpiData, KPI_ID } from "../../../../services/AboEnrichment/AboEnrichmentAPI.types";
import LeafRectangleLoader from "../../../../common/leaf/LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import { useTranslation } from "react-i18next";
import { formatDateKpi } from "../../../../common/util/formatDate";
import { BUSINESS_NATURE, CUSTOMER_BUSINESS_NATURES } from "../../../../common/enums/businessNatures";

const AboName = ({ aboName }: { aboName?: string | null }) => {
  const { t } = useTranslation(["leafAboName"]);
  if (!aboName || aboName === "" || aboName === "--") {
    return `${t("ConfidentialUser", "Confidential User", { ns: "leafAboName" })}`;
  }

  return aboName;
};

const CUSTOMER_ALLOWED_KPIS = new Set([
  KPI_ID.PERSONAL_PV,
  KPI_ID.PERSONAL_PV_REQ,
  KPI_ID.PERSONAL_PV_REQ_AR,
  KPI_ID.ANNUAL_PPV,
  KPI_ID.AUTO_RENEWAL,
  KPI_ID.SPONSOR_NAME,
  KPI_ID.RENEWAL_DATE,
]);

const PV_REQ_KPI_IDS = new Set([KPI_ID.PERSONAL_PV_REQ, KPI_ID.PERSONAL_PV_REQ_AR]);

const getPvRequirementKpiValue = (kpiId: string, isCustomer: boolean, data?: AboKpiData) => {
  const pvReq =
    kpiId === KPI_ID.PERSONAL_PV_REQ_AR
      ? (data?.ppvARRequirement ?? data?.personalPVRequirement)
      : data?.personalPVRequirement;
  if (!pvReq) return 0;
  const targetValue = pvReq.target?.value ?? 0;
  const targetFormatted = pvReq.target?.formatted;
  const volumeValue = pvReq.volume?.total?.value ?? 0;
  const volumeFormatted = pvReq.volume?.total?.formatted;
  return !isCustomer && targetValue > 0 ? (
    <DonutProgressCards
      currentAmount={Math.round(volumeValue * 100) / 100}
      currentAmountFormatted={volumeFormatted}
      targetAmount={targetValue}
      targetAmountFormatted={targetFormatted}
      met={volumeValue > 0 && targetValue > 0 && volumeValue >= targetValue}
      label={""}
      ariaTitle={"PPV Requirement"}
    />
  ) : (
    (volumeFormatted ?? 0)
  );
};

export const getKpiValue = (
  kpiId: string,
  kpiValue = "",
  skipEnrichment = false,
  data?: AboKpiData,
  locale = "en-US",
  businessNature = "",
) => {
  const isCustomer = CUSTOMER_BUSINESS_NATURES.has(Number(businessNature));
  if (isCustomer && !CUSTOMER_ALLOWED_KPIS.has(kpiId)) {
    return "--";
  }

  if (!data && skipEnrichment === false) {
    // No enrichment data yet. Show the pre-formatted row value if available,
    // otherwise show a loader. CBR_PERIOD_REMAINING and SAQ_STATUS need the
    // switch case for their icon rendering — let them fall through.
    if (!kpiValue) {
      return <LeafRectangleLoader isLoading width="6.25rem" height="1rem" margin="0" />;
    }
    if (kpiId !== KPI_ID.CBR_PERIOD_REMAINING && kpiId !== KPI_ID.SAQ_STATUS) {
      return kpiValue;
    }
  }

  if (PV_REQ_KPI_IDS.has(kpiId)) {
    return getPvRequirementKpiValue(kpiId, isCustomer, data);
  }

  switch (kpiId) {
    case KPI_ID.BIRTHDATE: {
      if (!kpiValue) return "--";
      try {
        const date = new Date(kpiValue.replace(/-/g, " "));
        const monthName = new Intl.DateTimeFormat(locale, { month: "long" }).format(date);
        const capitalizedMonth = monthName.replace(/^\p{L}/u, (c) => c.toLocaleUpperCase(locale));
        return new Intl.DateTimeFormat(locale, { month: "long", day: "numeric" })
          .format(date)
          .replace(monthName, capitalizedMonth);
      } catch {
        return kpiValue;
      }
    }
    case KPI_ID.PERSONAL_PV:
      // For customer rows the API sources personalPV from their own order PV, so no branch needed here.
      return data?.personalPV?.total.formatted ?? 0;
    case KPI_ID.PERSONAL_PV_YTD:
      return data?.personalPVYtd?.total.formatted ?? 0;
    case KPI_ID.GROUP_PV:
      return data?.groupPV?.total?.formatted ?? 0;
    case KPI_ID.GROUP_PV_NEEDED:
      return data?.groupPVNeeded?.formatted ?? 0;
    case KPI_ID.GROUP_SIZE:
      return data?.totalABOCount?.value ?? 0;
    case KPI_ID.SPONSORING:
      return data?.totalNewABOCount?.value ?? 0;
    case KPI_ID.SPONSORING_YTD:
      return data?.newABOCountYtd?.value ?? 0;
    case KPI_ID.AUTO_RENEWAL:
      // Prime Customers (Member business nature) are not eligible for auto renewal,
      // so show the "not applicable" placeholder rather than a red X ("disabled").
      if (Number(businessNature) === BUSINESS_NATURE.MEMBER) return "--";
      return data?.autoRenewal ? <IconSquareCheck /> : <IconError />;
    case KPI_ID.SAQ_STATUS:
      return (data?.saqStatus ?? (kpiValue === "qualified" || kpiValue === "true")) ? (
        <IconSquareCheck />
      ) : (
        <IconError />
      );
    case KPI_ID.CUSTOMER_COUNT:
      return data?.totalCustomerCount?.value ?? 0;
    case KPI_ID.SPONSOR_NAME:
      return <AboName aboName={data?.sponsorName} />;
    case KPI_ID.FRONTLINES:
      return data?.totalFrontlineCount?.value ?? "--";
    case KPI_ID.ANNUAL_PPV:
      // For customer rows the API sources annualPPV from their order PV for the PY, so no branch needed here.
      return data?.annualPPV?.formatted ?? "--";
    case KPI_ID.RENEWAL_DATE:
      return formatDateKpi(data?.renewalDate, locale);
    case KPI_ID.GROUP_BV:
      return data?.groupBV?.total?.formatted ?? 0;
    case KPI_ID.RUBY_PV:
      return data?.rubyPV?.total?.formatted ?? 0;
    case KPI_ID.BONUS_PERCENT:
      return data?.bonusPercent?.formatted ?? 0;
    case KPI_ID.QUALIFIED_LEGS:
      return data?.qualifiedLegs?.value ?? 0;
    case KPI_ID.CUSTOMER_PV:
      return data?.customerPV?.total?.formatted ?? 0;
    case KPI_ID.PERSONAL_ORDERS_COUNT:
      return data?.personalOrdersCount?.value ?? 0;
    case KPI_ID.GROUP_ORDERS_COUNT:
      return data?.groupOrdersCount?.value ?? 0;
    case KPI_ID.VCS_PV:
      return data?.vcsPV?.total?.formatted ?? 0;
    case KPI_ID.NON_VCS_PV:
      return data?.nonVCSPV?.total?.formatted ?? 0;
    case KPI_ID.NON_BONUSABLE_BV:
      return data?.nonBonusableBV?.total?.formatted ?? 0;
    case KPI_ID.VCS_PERCENT:
      return data?.personalVcsPercent?.formatted ?? 0;
    case KPI_ID.CBR_PERIOD_REMAINING:
      if (!kpiValue || kpiValue === "--") return "--";
      if (kpiValue === "0") {
        return (
          <span className="cbr-kpi--warning">
            <IconWarningTriangle /> {kpiValue}
          </span>
        );
      }
      return kpiValue;
    case KPI_ID.CBR_VCS_PERCENT:
    case KPI_ID.CBR_START_MONTH:
    case KPI_ID.CBR_END_MONTH:
      return kpiValue || "--";
    // FSI Action Report's PPV/VCS PV columns are pre-formatted as "{actual}/{target}"
    // (row data comes from the FSI-specific coreplusdetails call, not ABO enrichment),
    // so the donut is built by parsing that string rather than reading `data`.
    case "ppvActual":
    case "vcsPvActual": {
      if (!kpiValue) return "--";
      const [currentStr, targetStr] = kpiValue.split("/");
      const currentAmount = parseFloat(currentStr);
      const targetAmount = parseFloat(targetStr);
      if (Number.isNaN(currentAmount) || Number.isNaN(targetAmount) || targetAmount <= 0) {
        return kpiValue;
      }
      return (
        <DonutProgressCards
          currentAmount={currentAmount}
          currentAmountFormatted={currentStr}
          targetAmount={targetAmount}
          targetAmountFormatted={targetStr}
          met={currentAmount >= targetAmount}
          label={""}
          ariaTitle={kpiId === "vcsPvActual" ? "VCS PV" : "PPV"}
        />
      );
    }
    default:
      // For custom KPIs or pre-formatted row values, return the value if present
      if (kpiValue) {
        return kpiValue;
      }
      return "--";
  }
};
